// DISCLAIMER
// Copyright 2026 ArangoDB GmbH, Cologne, Germany

import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Grid, Message, Modal } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import { ChartDiagnostic, DeploymentChartLayers, DeploymentChartOverrides } from "../../api/charts/v1/icharts";
import CopyToClipboardButton from "../../ui/_copybutton";
import { decodeValues, DiagnosticsList, downloadText, encodeValues, errorText, hasErrors, parseValuesText, SourceLabel } from "../charts/utils";

const { PreviewDeploymentChart } = apiClients.idashboardClient;

const mono: React.CSSProperties = { fontFamily: "monospace" };

interface ChartOverridesModalArgs {
  deploymentId: string;
  // The chart to edit, none to add a chart that is not one of the default charts.
  layers?: DeploymentChartLayers;
  existingNames: string[];
  defaultNames: string[];
  canEdit: boolean;
  onSave: (name: string, overrides: DeploymentChartOverrides) => void;
  onClose: () => void;
}

// ReadOnlyPane shows one layer of a chart.
const ReadOnlyPane = ({ title, hint, text }: { title: React.ReactNode; hint: string; text: string }) => (
  <Form.Field>
    <label>{title}</label>
    <textarea rows={14} readOnly spellCheck={false} style={{ ...mono, background: "#f7f7f7" }} value={text} placeholder="No values." />
    <small>{hint}</small>
  </Form.Field>
);

// ChartOverridesModal edits the deployment overrides of a chart. Below them the layers it is applied over are shown,
// and what the chart resolves to, which is checked by chartd while editing.
const ChartOverridesModal = ({ deploymentId, layers, existingNames, defaultNames, canEdit, onSave, onClose }: ChartOverridesModalArgs) => {
  const isNew = !layers;
  const inDefaults = isNew ? false : !!layers!.in_defaults;
  const defaultChart = layers && layers.default_chart;
  const overrides = (layers && layers.overrides) || {};

  const [name, setName] = useState((layers && layers.name) || "");
  const [version, setVersion] = useState(overrides.version || "");
  const [enabled, setEnabled] = useState<string>(overrides.has_enabled ? (overrides.enabled ? "true" : "false") : "");
  const [values, setValues] = useState(decodeValues(overrides.values));
  const [diagnostics, setDiagnostics] = useState<ChartDiagnostic[]>();
  const [compiled, setCompiled] = useState("");
  const [checkError, setCheckError] = useState<any>();
  const [checking, setChecking] = useState(false);
  const request = useRef(0);

  const valuesValid = parseValuesText(values) !== undefined;
  const trimmed = name.trim();
  const nameTaken = isNew && existingNames.includes(trimmed);
  const nameIsDefault = isNew && defaultNames.includes(trimmed);
  const current = (): DeploymentChartOverrides => ({
    version: version.trim() || undefined,
    has_enabled: enabled !== "",
    enabled: enabled === "true",
    values: encodeValues(values),
  });

  // Let chartd resolve and check the overrides while editing.
  useEffect(() => {
    if (!trimmed || !valuesValid || nameTaken || nameIsDefault) {
      setDiagnostics(undefined);
      setCompiled("");
      setChecking(false);
      return;
    }
    const id = ++request.current;
    setChecking(true);
    const timer = setTimeout(async () => {
      try {
        const response = await PreviewDeploymentChart({ deployment_id: deploymentId, name: trimmed, overrides: current() });
        if (id !== request.current) return;
        setDiagnostics(response.diagnostics || []);
        setCompiled(response.yaml || "");
        setCheckError(undefined);
      } catch (err) {
        if (id !== request.current) return;
        setCheckError(err);
      }
      setChecking(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [trimmed, version, enabled, values]);

  const blocked = !trimmed || !valuesValid || nameTaken || nameIsDefault || hasErrors(diagnostics) || (isNew && !version.trim());
  const defaultEnabled = defaultChart && (defaultChart.enabled ? "enabled" : "disabled");

  return (
    <Modal open onClose={onClose} size="fullscreen">
      <Modal.Header>{isNew ? "Add chart" : `Edit ${name}`}</Modal.Header>
      <Modal.Content scrolling>
        <Message info size="small">
          {isNew
            ? "A chart that is not one of the default charts belongs to this deployment only, and is entirely made of the overrides below."
            : "The overrides are the only part of the chart that belongs to this deployment. Everything else follows the template and the configuration, and is refreshed on sync."}
        </Message>
        {isNew && (
          <Form>
            <Form.Input
              required
              label="Name"
              value={name}
              error={
                nameTaken ? "This chart is already on the deployment" : nameIsDefault ? "This is a default chart, add it from the available charts" : undefined
              }
              onChange={(_, { value }) => setName(value)}
            />
          </Form>
        )}
        <Grid columns={3} stackable>
          <Grid.Column>
            <Form>
              {inDefaults && defaultChart ? (
                <ReadOnlyPane
                  title={
                    <>
                      Template <SourceLabel source="template" />
                    </>
                  }
                  hint={`Version ${defaultChart.template_version}, ${
                    defaultChart.template_enabled ? "autostart" : "no autostart"
                  }. From charts.yaml of this release.`}
                  text={decodeValues(defaultChart.template_values)}
                />
              ) : (
                <Message size="small" content="This chart is not in the template." />
              )}
            </Form>
          </Grid.Column>
          <Grid.Column>
            <Form>
              {inDefaults && defaultChart ? (
                <ReadOnlyPane
                  title={
                    <>
                      Configuration <SourceLabel source="configuration" />
                    </>
                  }
                  hint="Set in the chart configuration of this environment, over the template."
                  text={decodeValues(defaultChart.configuration_values)}
                />
              ) : (
                <Message size="small" content="This chart is not in the chart configuration." />
              )}
            </Form>
          </Grid.Column>
          <Grid.Column>
            <Form>
              <Form.Field error={!valuesValid}>
                <label>
                  Deployment overrides <SourceLabel source="deployment" />
                </label>
                <textarea
                  rows={14}
                  spellCheck={false}
                  readOnly={!canEdit}
                  style={mono}
                  placeholder={"replicas: 2"}
                  value={values}
                  onChange={(e) => setValues(e.target.value)}
                />
                <small>
                  {valuesValid ? "Helm values as YAML, merged over the layers on the left. Only put what differs here." : "The values must be a YAML mapping."}
                </small>
              </Form.Field>
            </Form>
          </Grid.Column>
        </Grid>
        <Form style={{ marginTop: "1rem" }}>
          <Form.Group widths="equal">
            <Form.Input
              required={isNew}
              label="Version"
              readOnly={!canEdit}
              value={version}
              placeholder={defaultChart ? `Follow the default (${defaultChart.version})` : ""}
              onChange={(_, { value }) => setVersion(value)}
            />
            <Form.Field>
              <label>Enabled</label>
              <select value={enabled} disabled={!canEdit} onChange={(e) => setEnabled(e.target.value)}>
                {!isNew && <option value="">{`Follow the default${defaultEnabled ? ` (${defaultEnabled})` : ""}`}</option>}
                {isNew && <option value="">Choose</option>}
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </select>
            </Form.Field>
          </Form.Group>
        </Form>
        {checkError && <Message negative size="small" content={errorText(checkError)} />}
        <DiagnosticsList diagnostics={diagnostics} />
        {compiled && (
          <>
            <h4 style={{ marginTop: "1rem" }}>Compiled chart</h4>
            <p>What the data cluster gets, with these overrides applied.</p>
            <pre style={{ ...mono, maxHeight: "18rem", overflow: "auto", background: "#f7f7f7", padding: "0.5rem" }}>{compiled}</pre>
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <CopyToClipboardButton content={compiled} disabled={!compiled} />
        <Button icon="download" content="Download compiled" disabled={!compiled} onClick={() => downloadText(`${trimmed}.yaml`, compiled)} />
        <Button onClick={onClose}>{canEdit ? "Cancel" : "Close"}</Button>
        {canEdit && (
          <Button
            primary
            icon="check"
            content="Save"
            loading={checking}
            disabled={blocked || checking || (isNew && enabled === "")}
            onClick={() => onSave(trimmed, current())}
          />
        )}
      </Modal.Actions>
    </Modal>
  );
};

export default ChartOverridesModal;
